import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';

/**
 * Hook personalizado para manejar toda la lógica de liquidación
 * @returns {Object} Estado y funciones de liquidación
 */
export const useLiquidacion = () => {
  const { user } = useAuth();

  // Estados principales
  const [trabajadoresPendientes, setTrabajadoresPendientes] = useState([]);
  const [pagosPendientes, setPagosPendientes] = useState([]);
  const [resumen, setResumen] = useState({});
  const [serviciosDetalle, setServiciosDetalle] = useState([]);
  const [egresosDetalle, setEgresosDetalle] = useState([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState(null);
  const [selectedPago, setSelectedPago] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para diálogos
  const [dialogos, setDialogos] = useState({
    liquidacion: false,
    pagoDetalle: false,
    nuevoEgreso: false
  });

  // Estados para formularios
  const [liquidacionForm, setLiquidacionForm] = useState({
    servicios_seleccionados: [],
    egresos_seleccionados: [],
    observaciones: ''
  });

  const [egresoForm, setEgresoForm] = useState({
    id_trabajador: '',
    tipo_egreso: 'ADELANTO',
    concepto: '',
    monto: '',
    observaciones: ''
  });

  const [pagoForm, setPagoForm] = useState({
    estado: '',
    metodo_pago: '',
    numero_operacion: '',
    observaciones: ''
  });

  // ===============================================
  // 📊 FUNCIONES DE DATOS
  // ===============================================

  /**
   * Obtiene trabajadores con servicios pendientes de liquidar
   */
  const fetchTrabajadoresPendientes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/liquidacion/servicios-pendientes`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setTrabajadoresPendientes(response.data);
    } catch (error) {
      setError('Error al cargar trabajadores pendientes');
      console.error('Error:', error);
    }
  }, [user.token]);

  /**
   * Obtiene pagos pendientes de confirmar
   */
  const fetchPagosPendientes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/liquidacion/pagos-pendientes`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setPagosPendientes(response.data);
    } catch (error) {
      setError('Error al cargar pagos pendientes');
      console.error('Error:', error);
    }
  }, [user.token]);

  /**
   * Obtiene resumen general de liquidaciones
   */
  const fetchResumen = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/liquidacion/resumen`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setResumen(response.data);
    } catch (error) {
      console.error('Error al cargar resumen:', error);
    }
  }, [user.token]);

  /**
   * Obtiene detalle de servicios y egresos de un trabajador
   * @param {number} idTrabajador - ID del trabajador
   */
  const fetchServiciosDetalle = useCallback(async (idTrabajador) => {
    try {
      const [serviciosRes, egresosRes] = await Promise.all([
        axios.get(
          `${config.API_BASE_URL}/api/liquidacion/servicios-trabajador/${idTrabajador}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        ),
        axios.get(
          `${config.API_BASE_URL}/api/liquidacion/egresos-trabajador/${idTrabajador}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        )
      ]);

      setServiciosDetalle(serviciosRes.data);
      setEgresosDetalle(egresosRes.data);
    } catch (error) {
      setError('Error al cargar detalles del trabajador');
      console.error('Error:', error);
    }
  }, [user.token]);

  /**
   * Obtiene detalle completo de un pago
   * @param {number} idPago - ID del pago
   */
  const fetchPagoDetalle = useCallback(async (idPago) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/liquidacion/pago-detalle/${idPago}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSelectedPago(response.data);
    } catch (error) {
      setError('Error al cargar detalle del pago');
      console.error('Error:', error);
    }
  }, [user.token]);

  // ===============================================
  // 💰 FUNCIONES DE LIQUIDACIÓN
  // ===============================================

  /**
   * Abre el diálogo de liquidación para un trabajador
   * @param {Object} trabajador - Datos del trabajador
   */
  const abrirLiquidacion = async (trabajador) => {
    setSelectedTrabajador(trabajador);
    await fetchServiciosDetalle(trabajador.id_trabajador);
    setLiquidacionForm({
      servicios_seleccionados: [],
      egresos_seleccionados: [],
      observaciones: ''
    });
    setDialogos({ ...dialogos, liquidacion: true });
  };

  /**
   * Genera la liquidación para el trabajador seleccionado
   */
  const generarLiquidacion = async () => {
    if (liquidacionForm.servicios_seleccionados.length === 0) {
      setError('Debe seleccionar al menos un servicio');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${config.API_BASE_URL}/api/liquidacion/generar-liquidacion/${selectedTrabajador.id_trabajador}`,
        liquidacionForm,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setSuccess('Liquidación generada exitosamente');
      setDialogos({ ...dialogos, liquidacion: false });

      // Refrescar datos
      await Promise.all([
        fetchTrabajadoresPendientes(),
        fetchPagosPendientes(),
        fetchResumen()
      ]);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al generar liquidación');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registra un nuevo egreso/adelanto
   */
  const registrarEgreso = async () => {
    if (!egresoForm.id_trabajador || !egresoForm.concepto || !egresoForm.monto) {
      setError('Complete todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${config.API_BASE_URL}/api/liquidacion/egresos`,
        egresoForm,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setSuccess('Egreso registrado exitosamente');
      setDialogos({ ...dialogos, nuevoEgreso: false });
      setEgresoForm({
        id_trabajador: '',
        tipo_egreso: 'ADELANTO',
        concepto: '',
        monto: '',
        observaciones: ''
      });

      await fetchTrabajadoresPendientes();
    } catch (error) {
      setError(error.response?.data?.error || 'Error al registrar egreso');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===============================================
  // 💳 FUNCIONES DE PAGOS
  // ===============================================

  /**
   * Abre el diálogo de detalle de pago
   * @param {Object} pago - Datos del pago
   */
  const abrirPagoDetalle = async (pago) => {
    setLoading(true);
    setError('');

    try {
      await fetchPagoDetalle(pago.id_pago);

      setPagoForm({
        estado: pago.estado === 'PENDIENTE' ? '' : pago.estado,
        metodo_pago: pago.metodo_pago || '',
        numero_operacion: pago.numero_operacion || '',
        observaciones: pago.observaciones || ''
      });

      setDialogos({ ...dialogos, pagoDetalle: true });
    } catch (error) {
      setError('Error al cargar detalle del pago');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cierra el diálogo de pago con limpieza completa
   */
  const cerrarModalPago = () => {
    setDialogos({ ...dialogos, pagoDetalle: false });
    setPagoForm({
      estado: '',
      metodo_pago: '',
      numero_operacion: '',
      observaciones: ''
    });
    setSelectedPago(null);
    setError('');
  };

  /**
   * Actualiza el estado de un pago
   */
  const actualizarEstadoPago = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.put(
        `${config.API_BASE_URL}/api/liquidacion/pago/${selectedPago.pago.id_pago}/estado`,
        pagoForm,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(response.data.mensaje || 'Estado del pago actualizado exitosamente');
      cerrarModalPago();

      // Refrescar datos
      await Promise.all([
        fetchPagosPendientes(),
        fetchTrabajadoresPendientes(),
        fetchResumen()
      ]);

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al actualizar pago:', error);

      let errorMessage = 'Error al actualizar pago';

      if (error.response?.status === 401) {
        errorMessage = 'Sesión expirada. Inicie sesión nuevamente.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Pago no encontrado.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Retornar todo el estado y funciones
  return {
    // Estados
    trabajadoresPendientes,
    pagosPendientes,
    resumen,
    serviciosDetalle,
    egresosDetalle,
    selectedTrabajador,
    selectedPago,
    loading,
    error,
    success,
    dialogos,
    liquidacionForm,
    egresoForm,
    pagoForm,

    // Setters
    setError,
    setSuccess,
    setDialogos,
    setLiquidacionForm,
    setEgresoForm,
    setPagoForm,

    // Funciones de datos
    fetchTrabajadoresPendientes,
    fetchPagosPendientes,
    fetchResumen,
    fetchServiciosDetalle,
    fetchPagoDetalle,

    // Funciones de liquidación
    abrirLiquidacion,
    generarLiquidacion,
    registrarEgreso,

    // Funciones de pagos
    abrirPagoDetalle,
    cerrarModalPago,
    actualizarEstadoPago
  };
};
